import React from 'react';
import c from 'classnames';
import { ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import PopperPositionedPopover, {
  Props as PopperPositionedPopoverProps,
} from '../../../popper/PopperPositionedPopover';
import SophisticatedTooltipContent from '../SophisticatedTooltipContent';
import s from './SophisticatedTooltipBase.scss';

interface Props
  extends Omit<PopperPositionedPopoverProps, 'className' | 'renderPopover'> {
  /**
   * Will determine which of our sophisticated tooltip variants we render.
   */
  size: 'medium' | 'large';

  /**
   * The main body text of the tooltip. Displays after the heading.
   */
  description?: string | React.ReactNode;

  /**
   * A slot that displays before the heading. Depending on size, this could be above or to the
   * side of the main content. Consider putting an image here.
   */
  prefix?: React.ReactNode;

  /**
   * Displays after the heading and description. Consider putting a `<SophisticatedTooltipLink />` here.
   */
  postfix?: React.ReactNode;

  /**
   * Limits the width of the tooltip.
   */
  width?: number;

  /**
   * Shows a close button in the top right of the tooltip
   */
  hasCloseButton?: boolean;

  /**
   * Forces the theme to the provided ThemeOption
   */
  forceTheme?: ThemeOptions;

  /**
   * Called when the tooltip is closed.
   */
  onClose?: () => void;

  /** A className we add to the root of the component */
  className?: string;

  /** A className for the tooltip content */
  contentClassName?: string;
}

interface PropsWithHeading extends Props {
  /**
   * The emphasized text in this tooltip. Displays before the description.
   *
   * If this isn't provided, `aria-label` is required
   */
  heading: string;
}

interface PropsWithAriaLabel extends Props {
  /**
   * An a11y label.
   *
   * This can be used in place of the required `heading` field. Try to make it descriptive of the tooltip.
   */
  'aria-label': string;
}

export type CombinedProps = PropsWithHeading | PropsWithAriaLabel;

/**
 * Provides the base or our implementation of the SophisticatedTooltip.
 *
 * This is separated out to make it easier to manage differing opening/closing logic between FTUEs and normal tooltips.
 */
const SophisticatedTooltipBase: React.FC<CombinedProps> = ({
  width,
  postfix,
  prefix,
  size,
  description,
  forceTheme,
  hasCloseButton,
  onClose,
  onReferenceClick,
  onReferenceMouseEnter,
  onReferenceMouseLeave,
  children,
  className,
  contentClassName,
  ...rest
}) => {
  const heading = (rest as PropsWithHeading).heading;
  const ariaLabel = (rest as PropsWithAriaLabel)['aria-label'];

  return (
    <PopperPositionedPopover
      className={c(s['root'], className)}
      onReferenceClick={onReferenceClick}
      onReferenceMouseEnter={onReferenceMouseEnter}
      onReferenceMouseLeave={onReferenceMouseLeave}
      renderPopover={popperProps => (
        <SophisticatedTooltipContent
          width={width}
          heading={heading}
          aria-label={ariaLabel}
          description={description}
          prefix={prefix}
          postfix={postfix}
          size={size}
          contentClassName={contentClassName}
          hasCloseButton={hasCloseButton}
          forceTheme={forceTheme}
          onMouseEnter={onReferenceMouseEnter}
          onMouseLeave={onReferenceMouseLeave}
          onClose={onClose}
          {...popperProps}
        />
      )}
      {...rest}
    >
      {children}
    </PopperPositionedPopover>
  );
};
SophisticatedTooltipBase.displayName = 'SophisticatedTooltipBase';

export default SophisticatedTooltipBase;
